/**
 * ColumnProfilePanel — Rich column profile drawer with quality bars and frequency histograms.
 *
 * Replaces the small WorkspaceColumnProfilePopover with a persistent MUI Drawer
 * that shows detailed column statistics, data quality indicators, and top value
 * frequency distribution.
 */
import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useWorkspaceColumnProfile } from '../../hooks/useWorkspaceSession';

export interface ColumnProfilePanelProps {
  sessionId: string;
  tableName: string;
  columnName: string;
  displayDtype?: string;
  /** When set, profile from this query (computed / transformed columns). */
  querySql?: string;
  open: boolean;
  onClose: () => void;
}

const DRAWER_WIDTH = 320;

const ColumnProfilePanel: React.FC<ColumnProfilePanelProps> = ({
  sessionId,
  tableName,
  columnName,
  displayDtype,
  querySql,
  open,
  onClose,
}) => {
  const { data: profile, isLoading } = useWorkspaceColumnProfile(
    sessionId,
    tableName,
    open ? columnName : null,
    open ? querySql : null,
    displayDtype,
  );

  const nullPct = profile?.null_pct ?? 0;
  const validPct = 1 - nullPct;
  const effectiveDtype = displayDtype || profile?.dtype;
  const cardinalityRatio = profile && profile.count > 0
    ? profile.unique_count / profile.count
    : 0;
  const maxTopCount = profile?.top_values?.length
    ? Math.max(...profile.top_values.map((tv) => tv.count))
    : 1;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          p: 2,
          borderLeft: 1,
          borderColor: 'divider',
          position: 'absolute',
        },
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontFamily: '"Fira Code", monospace', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {columnName}
          </Typography>
          {effectiveDtype && (
            <Chip label={effectiveDtype} size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
          )}
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {isLoading && (
        <Typography variant="caption" color="text.secondary">Loading profile…</Typography>
      )}

      {profile && (
        <Stack spacing={2}>
          {/* Quality Bar */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Data Quality
            </Typography>
            <Tooltip title={`${(validPct * 100).toFixed(1)}% valid, ${(nullPct * 100).toFixed(1)}% null`}>
              <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
                <Box sx={{ width: `${validPct * 100}%`, bgcolor: '#22c55e', transition: 'width 0.3s' }} />
                <Box sx={{ width: `${nullPct * 100}%`, bgcolor: '#ef4444', transition: 'width 0.3s' }} />
              </Box>
            </Tooltip>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#22c55e', fontSize: 10 }}>
                {(validPct * 100).toFixed(1)}% valid
              </Typography>
              <Typography variant="caption" sx={{ color: '#ef4444', fontSize: 10 }}>
                {(nullPct * 100).toFixed(1)}% null
              </Typography>
            </Stack>
          </Box>

          <Divider />

          {/* Stats Grid */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Statistics
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <StatRow label="Count" value={profile.count.toLocaleString()} />
              <StatRow label="Nulls" value={`${profile.null_count.toLocaleString()} (${(nullPct * 100).toFixed(1)}%)`} />
              <StatRow label="Unique" value={profile.unique_count.toLocaleString()} />
              {profile.min != null && <StatRow label="Min" value={String(profile.min)} />}
              {profile.max != null && <StatRow label="Max" value={String(profile.max)} />}
              {profile.mean != null && <StatRow label="Mean" value={profile.mean.toFixed(2)} />}
              {profile.std != null && <StatRow label="Std Dev" value={profile.std.toFixed(2)} />}
            </Box>
          </Box>

          <Divider />

          {/* Cardinality */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Cardinality</Typography>
              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600 }}>
                {cardinalityRatio < 0.1 ? 'Low' : cardinalityRatio < 0.5 ? 'Medium' : 'High'}
              </Typography>
            </Stack>
            <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
              <Box sx={{ width: `${cardinalityRatio * 100}%`, height: '100%', bgcolor: '#6366f1', borderRadius: 2 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mt: 0.25, display: 'block' }}>
              {profile.unique_count.toLocaleString()} unique / {profile.count.toLocaleString()} total ({(cardinalityRatio * 100).toFixed(1)}%)
            </Typography>
          </Box>

          {/* Top Values with Frequency Bars */}
          {profile.top_values && profile.top_values.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Top Values
                </Typography>
                <Stack spacing={0.75}>
                  {profile.top_values.slice(0, 10).map((tv, i) => {
                    const pct = maxTopCount > 0 ? (tv.count / maxTopCount) * 100 : 0;
                    return (
                      <Box key={i}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 11,
                              fontFamily: '"Fira Code", monospace',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 200,
                            }}
                          >
                            {tv.value || '(empty)'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, flexShrink: 0, ml: 1 }}>
                            {tv.count.toLocaleString()}
                          </Typography>
                        </Stack>
                        <Box sx={{ height: 14, bgcolor: '#f1f5f9', borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#3b82f6', borderRadius: 1, transition: 'width 0.3s' }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      )}
    </Drawer>
  );
};

const StatRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 500, fontFamily: '"Fira Code", monospace' }}>
      {value}
    </Typography>
  </Box>
);

export default ColumnProfilePanel;
